<script setup lang="ts">
import { computed, ref } from 'vue';

import { formatDayMonth, formatMoney } from '@/shared/lib';

import type { ChartGranularity, ChartPoint } from './chart-point-window';

import { formatChartPointWindow } from './chart-point-window';
import { buildExtentScale, buildIndexScale } from './chart-scales';
import ChartTooltip from './ChartTooltip.vue';
import { useChartGeometry } from './use-chart-geometry';
import { useChartPointer } from './use-chart-pointer';

interface Props {
  points: ChartPoint[];
  granularity: ChartGranularity;
  label?: string;
  /** Формат значения в подсказке; по умолчанию — деньги. */
  valueFormatter?: (value: number) => string;
}

const props = withDefaults(defineProps<Props>(), {
  label: 'Динамика показателя',
  valueFormatter: (value: number) => formatMoney(value) ?? '—',
});

const PADDING = 3;
const TOOLTIP_GAP = 4;
const svgRef = ref<SVGSVGElement | null>(null);
const { geometry, isMeasured } = useChartGeometry(svgRef);
const { activeIndex, activeSvgBounds, handlePointerLeave, handlePointerMove } = useChartPointer({
  bounds: (svgBounds) => ({
    left: svgBounds.left + PADDING,
    width: Math.max(0, geometry.value.viewWidth - PADDING * 2),
  }),
  count: () => props.points.length,
  isMeasured,
});

const coordinates = computed(() => {
  if (props.points.length === 0) {
    return [];
  }

  const values = props.points.map((point) => point.value);
  const xScale = buildIndexScale(props.points.length, [PADDING, geometry.value.viewWidth - PADDING]);
  // Спарклайн намеренно сохраняет min–max нормировку: смена семантики требует отдельного продуктового решения.
  const valueScale = buildExtentScale(values, [geometry.value.viewHeight - PADDING, PADDING]);

  return props.points.map((point, index) => {
    return {
      x: xScale(index),
      y: valueScale(point.value),
    };
  });
});

const polylinePoints = computed(() => coordinates.value.map(({ x, y }) => `${x.toFixed(2)},${y.toFixed(2)}`).join(' '));
const activeCoordinate = computed(() =>
  activeIndex.value === null ? null : (coordinates.value[activeIndex.value] ?? null),
);
const activePoint = computed(() => (activeIndex.value === null ? null : (props.points[activeIndex.value] ?? null)));
// Тултип уходит в body: в ячейке таблицы его обрезает overflow обёртки и перекрывает липкая шапка.
const tooltipAnchor = computed(() =>
  activeCoordinate.value && activeSvgBounds.value
    ? {
        x: activeSvgBounds.value.left + activeCoordinate.value.x,
        y: activeSvgBounds.value.top - TOOLTIP_GAP,
      }
    : null,
);
const windowLabel = computed(() => {
  const first = props.points[0];
  const last = props.points.at(-1);

  return first && last ? `${formatDayMonth(first.date)}–${formatDayMonth(last.date)}` : 'без данных';
});
const ariaLabel = computed(() => `${props.label}. Окно: ${windowLabel.value}.`);

const tooltipText = computed(() => {
  if (!activePoint.value) {
    return '';
  }

  return `${formatChartPointWindow(activePoint.value.date, props.granularity)} · ${props.valueFormatter(
    activePoint.value.value,
  )}`;
});
</script>

<template>
  <div class="relative">
    <svg
      ref="svgRef"
      class="block h-11 w-full touch-pan-y overflow-visible"
      role="img"
      :aria-label="ariaLabel"
      @pointermove="handlePointerMove"
      @pointerleave="handlePointerLeave"
      @pointercancel="handlePointerLeave"
    >
      <template v-if="isMeasured">
        <polyline
          v-if="polylinePoints"
          :points="polylinePoints"
          fill="none"
          stroke="currentColor"
          stroke-width="2"
          stroke-linecap="round"
          stroke-linejoin="round"
          vector-effect="non-scaling-stroke"
        />

        <g
          v-if="activeCoordinate"
          aria-hidden="true"
        >
          <line
            :x1="activeCoordinate.x"
            :x2="activeCoordinate.x"
            :y1="PADDING"
            :y2="geometry.viewHeight - PADDING"
            stroke="currentColor"
            stroke-width="1"
            stroke-dasharray="2 2"
            opacity="0.35"
            vector-effect="non-scaling-stroke"
          />
          <circle
            :cx="activeCoordinate.x"
            :cy="activeCoordinate.y"
            r="3"
            fill="var(--base-container-bg)"
            stroke="currentColor"
            stroke-width="2"
            vector-effect="non-scaling-stroke"
          />
        </g>
      </template>
    </svg>

    <ChartTooltip
      v-if="activeCoordinate && tooltipAnchor"
      :anchor-percent="(activeCoordinate.x / geometry.viewWidth) * 100"
      :viewport-x="tooltipAnchor.x"
      :viewport-y="tooltipAnchor.y"
      placement="above"
    >
      {{ tooltipText }}
    </ChartTooltip>
  </div>
</template>
